/**
 * @description 存放所有共享的常量
 */

/** 饼图最多显示的饼块数量，超过的合并为"其他" */
export const PIE_CHART_DATA_LIMIT = 15

/**
 * 地图分析的两个子 tab 各自对应哪些 location_log.path。
 *
 * 服务端按分组过滤后再取样本，所以每个 tab 独享 `MAP_LOCATION_SAMPLE_LIMIT` 的额度；
 * 从前只有一条不分组的查询，几个 tab 挤同一个上限，请求多的那天会互相挤掉。
 */
export const MAP_PATH_GROUPS = {
  regeo: ['/regeocode-amap', '/regeocode-gmap'],
  search: ['/search-text'],
} as const

export type MapPathGroup = keyof typeof MAP_PATH_GROUPS

export const MAP_PATH_GROUP_IDS = Object.keys(MAP_PATH_GROUPS) as MapPathGroup[]

/**
 * 地图上最多画多少个点。纯粹是渲染上限——统计数字不受它影响，
 * 用户分布和总数走单独的聚合查询，见 `/map-locations` 的 userStats / totalCount。
 */
export const MAP_LOCATION_SAMPLE_LIMIT = 10000

/** /admin/revenuecat 按 appUserID 查 rc_proxy_log 时最多返回的行数 */
export const RC_PROXY_APP_USER_LOG_LIMIT = 200

/** PostHog 项目（Deer）。服务端拿它拼 HogQL 查询接口，后台页面拿它拼「在 PostHog 查看」的链接 */
export const POSTHOG_PROJECT_ID = 272924
export const POSTHOG_PROJECT_URL = `https://us.posthog.com/project/${POSTHOG_PROJECT_ID}`

/** 判断数值类型的这一列是否属于维度（年份）[数值类型的值去重后 与 原始数据数量的比例]  */
export const IS_DIMENSION_PROPORTION = 0

/** GPT 上下文中保留的对话数（一问一答) */
export const GPT_MESSAGE_REMAIN_NUM = 3

/** GPT 超时 10分钟 */
export const GPT_TIMEOUT = 10 * 60 * 1000

/** 默认接口超时 3 分钟 */
export const DEFAULT_TIMEOUT = 3 * 60 * 1000

/** 如果 select 没有 limit，则添加默认的 limit 数量 */
export const DEFAULT_SELECT_LIMIT = 100

export const CHART_TYPES = ['Kpi', 'SimpleTable', 'LineChart', 'PieChart', 'MultiLineChart'] as const

// 客户端安全的图表配置（不包含SQL查询）
export const ChartConfigs = {
  // error-log 表的信息
  'today-error-count': {
    chartTitle: '今天的错误数',
    chartType: 'Kpi',
  },
  '7-day-error-count': {
    chartTitle: '近7天的错误数',
    chartType: 'Kpi',
  },
  '30-day-error-count': {
    chartTitle: '近30天的错误数',
    chartType: 'Kpi',
  },
  'all-day-error-count': {
    chartTitle: '所有天的总错误数',
    chartType: 'Kpi',
  },
  '30-day-error-details': {
    chartTitle: '近30天的错误详情',
    chartType: 'SimpleTable',
  },

  // Location 相关的图表配置
  'location-total-requests': {
    chartTitle: '总请求次数',
    chartType: 'Kpi',
  },
  'location-today-requests': {
    chartTitle: '今日请求次数',
    chartType: 'Kpi',
  },
  'location-success-requests': {
    chartTitle: '成功请求次数',
    chartType: 'Kpi',
  },
  'location-failed-requests': {
    chartTitle: '失败请求次数',
    chartType: 'Kpi',
  },
  'location-unique-users': {
    chartTitle: '累计设备数',
    chartType: 'Kpi',
  },
  'location-30-day-trend': {
    chartTitle: '最近30天调用次数趋势',
    chartType: 'MultiLineChart',
  },
  'location-language-distribution': {
    chartTitle: '语言分布',
    chartType: 'PieChart',
  },
  'location-recent-details': {
    chartTitle: '最近100条位置请求详情',
    chartType: 'SimpleTable',
  },
  'location-30-day-error-trend': {
    chartTitle: '最近30天错误趋势',
    chartType: 'LineChart',
  },
  'location-30-day-user-trend': {
    chartTitle: '最近30天每日设备数',
    chartType: 'LineChart',
  },

  // 外部地图 API 的配额监控。高德免费额度里 place/around 和 place/detail 都只有
  // 1,000 次/日，撞线时返回 HTTP 200 + status:"0"，用户侧只表现为「地点变少了」。
  // 这几个图是唯一能提前看见撞线的地方。
  'map-api-today-errors': {
    chartTitle: '今日地图 API 业务错误数',
    chartType: 'Kpi',
  },
  'map-api-today-paid-calls': {
    chartTitle: '今日付费调用数',
    chartType: 'Kpi',
  },
  'map-api-today-success-rate': {
    chartTitle: '今日成功率',
    chartType: 'Kpi',
  },
  'map-api-today-cache-hit-rate': {
    chartTitle: '今日缓存命中率',
    chartType: 'Kpi',
  },
  'map-api-today-usage': {
    chartTitle: '今日各 endpoint 调用量与配额水位',
    chartType: 'SimpleTable',
  },
  'map-api-30-day-calls': {
    chartTitle: '最近30天调用量（按 provider）',
    chartType: 'MultiLineChart',
  },
  'map-api-30-day-success-rate': {
    chartTitle: '最近30天成功率（按 provider）',
    chartType: 'MultiLineChart',
  },
  'map-api-30-day-error-trend': {
    chartTitle: '最近30天地图 API 错误趋势',
    chartType: 'LineChart',
  },

  // Google Maps Platform 的费用。单独拎出来是因为 GCP 的赠金/试用金**不抵扣**
  // Maps Platform，整个 GCP 账单里只有这部分是真金白银 —— 2026-08 的 28.61 SGD
  // 全部来自这里。免费额度按 SKU 算、自然月重置，超一次算一次钱，所以要盯着水位。
  'map-api-google-month-cost': {
    chartTitle: '本月 Google 地图预计费用',
    chartType: 'Kpi',
  },
  'map-api-google-month-cost-detail': {
    chartTitle: '本月 Google 各 SKU 用量与预计费用',
    chartType: 'SimpleTable',
  },

  // RevenueCat 反代（/v1）的 rc_proxy_log。只记收据等非 GET、>=400、出错、>5s、客户端先断开的请求，
  // 量大的成功 GET 不在表里，所以这里的数都是「异常」的口径，不是总请求量。
  'rc-proxy-30-day-trend': {
    chartTitle: '最近30天 RevenueCat 代理趋势',
    chartType: 'MultiLineChart',
  },
  'rc-proxy-recent-issues': {
    chartTitle: '最近100条出错 / 慢 / 客户端先断开的请求',
    chartType: 'SimpleTable',
  },
} as const

export type ChartConfigIdType = keyof typeof ChartConfigs
export const ChartConfigIds = Object.keys(ChartConfigs) as ChartConfigIdType[]

/**
 * /admin/usage 的时间范围。today / yesterday 按小时分桶，其余按天；
 * 对比的都是紧挨着的前一段同样长的时间。
 */
export const USAGE_RANGES = ['today', 'yesterday', '7d', '30d'] as const

/** /admin/timeline-images 概览可选的天数 */
export const TIMELINE_STATS_DAYS = [7, 30, 90] as const

/**
 * /admin/usage 能按哪些维度过滤。country 用的是 ISO 国家码（$geoip_country_code），
 * 不是国家名：国家名里有撇号，码不会有。
 */
export const USAGE_FILTER_KEYS = ['country', 'osVersion', 'appVersion', 'event'] as const

/**
 * /admin/usage 怎么对待 TestFlight 的事件（iOS 每条事件都带超级属性 is_testflight）。
 * 默认 exclude：TF 只有几台开发机，却能占到事件数的两成多，混进来就看不出真实用户在干什么。
 * 本地装到真机的 Release 包也是 sandboxReceipt，和 TF 一样带 true；Debug 包根本不发 PostHog。
 */
export const USAGE_TESTFLIGHT_MODES = ['exclude', 'include', 'only'] as const

/**
 * /admin/usage「注册与付费」卡片看的事件，按漏斗顺序。名字和 iOS 埋点一致（失败是 purchase_failure，不是 failed）。
 */
export const USAGE_FUNNEL_EVENTS = [
  'sign_up',
  'first_location',
  'paywall_view',
  'purchase_started',
  'purchase_completed',
  'purchase_failure',
  'purchase_cancelled',
] as const
